import type { CbiFrontendServiceContext } from "../../frontend/context/CbiFrontendServiceContext";
import type { ApClassTypeStaff } from "./ApClassTypeStaff";
import type { JpClassTypeStaff } from "./JpClassTypeStaff";
import type { RatingDTOBase } from "./RatingDTOBase";
import type { RatingRef } from "./RatingRef";
import type { TrackedBase } from "./TrackedBase";

export class RatingStaff implements RatingDTOBase, TrackedBase {
  constructor(public readonly ratingId: number, public readonly ratingName: string | null, public readonly displayOrder: number | null, public readonly active: string | null, public readonly ratingCategory: string | null, public readonly overriddenBy: RatingStaff | null, public readonly createdOn: Date | null, public readonly createdBy: string | null, public readonly updatedOn: Date | null, public readonly updatedBy: string | null, public readonly typesRequiringThisRatingAsPrereq: Array<ApClassTypeStaff> | null, public readonly typesRequiringThisRatingAsOverkill: Array<ApClassTypeStaff> | null, public readonly typesTeachableWithThisRating: Array<ApClassTypeStaff> | null, public readonly jpTypesRequiringThisRatingAsPrereq: Array<JpClassTypeStaff> | null, public readonly jpTypesRequiringThisRatingAsOverkill: Array<JpClassTypeStaff> | null, public readonly jpTypesTeachableWithThisRating: Array<JpClassTypeStaff> | null, public readonly overriddenByTargetIds: RatingRef | null) {
    this.cbiServiceContext = null;
    this.overriddenByLoaded = this.overriddenBy != null;
    this.overriddenByCached = this.overriddenBy;
    this.typesRequiringThisRatingAsPrereqLoaded = this.typesRequiringThisRatingAsPrereq != null;
    this.typesRequiringThisRatingAsPrereqCached = this.typesRequiringThisRatingAsPrereq;
    this.typesRequiringThisRatingAsOverkillLoaded = this.typesRequiringThisRatingAsOverkill != null;
    this.typesRequiringThisRatingAsOverkillCached = this.typesRequiringThisRatingAsOverkill;
    this.typesTeachableWithThisRatingLoaded = this.typesTeachableWithThisRating != null;
    this.typesTeachableWithThisRatingCached = this.typesTeachableWithThisRating;
    this.jpTypesRequiringThisRatingAsPrereqLoaded = this.jpTypesRequiringThisRatingAsPrereq != null;
    this.jpTypesRequiringThisRatingAsPrereqCached = this.jpTypesRequiringThisRatingAsPrereq;
    this.jpTypesRequiringThisRatingAsOverkillLoaded = this.jpTypesRequiringThisRatingAsOverkill != null;
    this.jpTypesRequiringThisRatingAsOverkillCached = this.jpTypesRequiringThisRatingAsOverkill;
    this.jpTypesTeachableWithThisRatingLoaded = this.jpTypesTeachableWithThisRating != null;
    this.jpTypesTeachableWithThisRatingCached = this.jpTypesTeachableWithThisRating;
  }
  cbiServiceContext: CbiFrontendServiceContext | null;
  private overriddenByLoaded: boolean;
  private overriddenByCached: RatingStaff | null;
  private typesRequiringThisRatingAsPrereqLoaded: boolean;
  private typesRequiringThisRatingAsPrereqCached: Array<ApClassTypeStaff> | null;
  private typesRequiringThisRatingAsOverkillLoaded: boolean;
  private typesRequiringThisRatingAsOverkillCached: Array<ApClassTypeStaff> | null;
  private typesTeachableWithThisRatingLoaded: boolean;
  private typesTeachableWithThisRatingCached: Array<ApClassTypeStaff> | null;
  private jpTypesRequiringThisRatingAsPrereqLoaded: boolean;
  private jpTypesRequiringThisRatingAsPrereqCached: Array<JpClassTypeStaff> | null;
  private jpTypesRequiringThisRatingAsOverkillLoaded: boolean;
  private jpTypesRequiringThisRatingAsOverkillCached: Array<JpClassTypeStaff> | null;
  private jpTypesTeachableWithThisRatingLoaded: boolean;
  private jpTypesTeachableWithThisRatingCached: Array<JpClassTypeStaff> | null;
  async overriddenByLazy(): Promise<RatingStaff | null> {
    if (!this.overriddenByLoaded) {
      this.overriddenByCached = await (this.overriddenByTargetIds != null ? this.cbiServiceContext!.ratingService().getRatingStaff(this.overriddenByTargetIds.ratingId) : Promise.resolve(null));
      this.overriddenByLoaded = true;
    }
    return this.overriddenByCached;
  }
  async typesRequiringThisRatingAsPrereqLazy(): Promise<Array<ApClassTypeStaff>> {
    if (!this.typesRequiringThisRatingAsPrereqLoaded) {
      this.typesRequiringThisRatingAsPrereqCached = await this.cbiServiceContext!.ratingService().getRatingStaffTypesRequiringThisRatingAsPrereq(this.ratingId);
      this.typesRequiringThisRatingAsPrereqLoaded = true;
    }
    return this.typesRequiringThisRatingAsPrereqCached!;
  }
  async typesRequiringThisRatingAsOverkillLazy(): Promise<Array<ApClassTypeStaff>> {
    if (!this.typesRequiringThisRatingAsOverkillLoaded) {
      this.typesRequiringThisRatingAsOverkillCached = await this.cbiServiceContext!.ratingService().getRatingStaffTypesRequiringThisRatingAsOverkill(this.ratingId);
      this.typesRequiringThisRatingAsOverkillLoaded = true;
    }
    return this.typesRequiringThisRatingAsOverkillCached!;
  }
  async typesTeachableWithThisRatingLazy(): Promise<Array<ApClassTypeStaff>> {
    if (!this.typesTeachableWithThisRatingLoaded) {
      this.typesTeachableWithThisRatingCached = await this.cbiServiceContext!.ratingService().getRatingStaffTypesTeachableWithThisRating(this.ratingId);
      this.typesTeachableWithThisRatingLoaded = true;
    }
    return this.typesTeachableWithThisRatingCached!;
  }
  async jpTypesRequiringThisRatingAsPrereqLazy(): Promise<Array<JpClassTypeStaff>> {
    if (!this.jpTypesRequiringThisRatingAsPrereqLoaded) {
      this.jpTypesRequiringThisRatingAsPrereqCached = await this.cbiServiceContext!.ratingService().getRatingStaffJpTypesRequiringThisRatingAsPrereq(this.ratingId);
      this.jpTypesRequiringThisRatingAsPrereqLoaded = true;
    }
    return this.jpTypesRequiringThisRatingAsPrereqCached!;
  }
  async jpTypesRequiringThisRatingAsOverkillLazy(): Promise<Array<JpClassTypeStaff>> {
    if (!this.jpTypesRequiringThisRatingAsOverkillLoaded) {
      this.jpTypesRequiringThisRatingAsOverkillCached = await this.cbiServiceContext!.ratingService().getRatingStaffJpTypesRequiringThisRatingAsOverkill(this.ratingId);
      this.jpTypesRequiringThisRatingAsOverkillLoaded = true;
    }
    return this.jpTypesRequiringThisRatingAsOverkillCached!;
  }
  async jpTypesTeachableWithThisRatingLazy(): Promise<Array<JpClassTypeStaff>> {
    if (!this.jpTypesTeachableWithThisRatingLoaded) {
      this.jpTypesTeachableWithThisRatingCached = await this.cbiServiceContext!.ratingService().getRatingStaffJpTypesTeachableWithThisRating(this.ratingId);
      this.jpTypesTeachableWithThisRatingLoaded = true;
    }
    return this.jpTypesTeachableWithThisRatingCached!;
  }
}
